// Texto 25: "Meus clientes" — a lista ÚNICA de clientes (aba 'clientes', coleção 'cliente').
// As abas de trabalho (hoje Portal do Cliente e Freelancer & Projetos) continuam com a ficha delas (onde ficam o grupo
// e o convite do Portal, os documentos, os pedidos, os cartões e as horas), mas cada uma fica LIGADA à ficha de
// "Meus clientes" pelo campo c.cid. Nome e contato vêm da ficha única: mudou num lugar, muda em todas.
// Nada é movido nem apagado na migração: só se cria a ficha única e a ligação.
import { Alert } from 'react-native';
import { getDoc, setDoc } from '../store/store';
import type { ModId, Reg } from '../store/types';
import { excluirReg, mudarCampos, novoReg, regsDe, salvarReg } from '../store/modulos';
import { gruposEmCache } from './grupos';
import { norm } from './date';
import { soDigitos } from './cpf';
import { t } from './i18n';

export const BASE: ModId = 'clientes';
/** Abas de trabalho que usam a lista única, e como os campos de lá se chamam (campo da aba ← campo de Meus clientes). */
export const ABAS_TRABALHO: { mod: ModId; col: string; campos: Record<string, (c: Record<string, any>) => any> }[] = [
  { mod: 'portal', col: 'cliente', campos: { nome: c => c.nome, telefone: c => c.telefone, email: c => c.email, documento: c => c.doc, contato: c => c.contatoNome } },
  { mod: 'freelancer', col: 'cliente', campos: { nome: c => c.nome, telefone: c => c.telefone, email: c => c.email, contato: c => c.contatoNome, endereco: c => enderecoTexto(c) } },
];
const trabalhoDe = (mod: ModId) => ABAS_TRABALHO.find(a => a.mod === mod);

export function enderecoTexto(c: Record<string, any>) {
  const rua = [c.rua, c.numero].filter(x => String(x ?? '').trim()).join(', ');
  return [rua, c.complemento, c.bairro, [c.cidade, c.uf].filter(Boolean).join(' – '), c.cep].map(x => String(x ?? '').trim()).filter(Boolean).join(', ');
}
export const clientesBase = () => regsDe(BASE).filter(r => r.col === 'cliente');

/** Ficha de outra pessoa (Portal em que EU sou o cliente): não entra na minha lista. */
function ehDeOutro(r: Reg) {
  if (!r.g) return false;
  const g = gruposEmCache().lista?.find(x => x.id === r.g);
  return !g || !g.admin;
}

// ─── Igualdade (para achar duplicados) ───
const tel = (v: unknown) => { const d = soDigitos(String(v ?? '')); return d.length >= 8 ? d.slice(-9) : ''; };
const mail = (v: unknown) => String(v ?? '').trim().toLowerCase();
const doc = (v: unknown) => { const d = soDigitos(String(v ?? '')); return d.length === 11 || d.length === 14 ? d : ''; };
const nome = (v: unknown) => norm(String(v ?? '')).replace(/\s+/g, ' ').trim();
/** Cliente já cadastrado com o mesmo CPF/CNPJ, telefone, e-mail ou (sem nada disso) o mesmo nome. */
export function duplicadoDe(c: Record<string, any>, ignorar?: string): { r: Reg; por: string } | null {
  for (const r of clientesBase()) {
    if (r.id === ignorar) continue;
    if (doc(c.doc ?? c.documento) && doc(c.doc ?? c.documento) === doc(r.c.doc)) return { r, por: 'doc' };
    if (tel(c.telefone) && (tel(c.telefone) === tel(r.c.telefone) || tel(c.telefone) === tel(r.c.telefone2))) return { r, por: 'telefone' };
    if (mail(c.email) && mail(c.email) === mail(r.c.email)) return { r, por: 'email' };
  }
  const n = nome(c.nome);
  if (n && !tel(c.telefone) && !mail(c.email) && !doc(c.doc ?? c.documento)) {
    const r = clientesBase().find(x => x.id !== ignorar && nome(x.c.nome) === n);
    if (r) return { r, por: 'nome' };
  }
  return null;
}

// ─── Ligação com as abas de trabalho ───
/** As fichas das abas de trabalho ligadas a este cliente. */
export function ligadas(cid: string): { mod: ModId; r: Reg }[] {
  return ABAS_TRABALHO.flatMap(a => regsDe(a.mod).filter(r => r.col === a.col && r.c.cid === cid).map(r => ({ mod: a.mod, r })));
}
/** A aba de trabalho está em uso (já tem alguma ficha)? */
const emUso = (mod: ModId) => regsDe(mod).length > 0;

/** Ficha de Meus clientes salva: os campos comuns vão para as fichas ligadas; nas abas em uso, cria a ficha que faltar. */
export function aoSalvarBase(r: Reg) {
  for (const a of ABAS_TRABALHO) {
    const lig = regsDe(a.mod).filter(x => x.col === a.col && x.c.cid === r.id);
    const valores = Object.fromEntries(Object.entries(a.campos).map(([k, f]) => [k, f(r.c) ?? '']));
    if (lig.length) for (const x of lig) { if (Object.entries(valores).some(([k, v]) => String(x.c[k] ?? '') !== String(v ?? ''))) mudarCampos(a.mod, x.id, valores); }
    else if (emUso(a.mod) && !(r.c.fora ?? []).includes(a.mod)) salvarReg(a.mod, novoReg(a.col, null, { ...valores, cid: r.id }));
  }
}
/** Ficha de cliente salva numa aba de trabalho: liga (ou cria) a ficha de Meus clientes e atualiza os campos comuns. */
export function aoSalvarNaAba(mod: ModId, r: Reg) {
  const a = trabalhoDe(mod);
  if (!a || ehDeOutro(r)) return;
  const base = r.c.cid ? clientesBase().find(x => x.id === r.c.cid) : null;
  const daAba = deAbaParaBase(mod, r.c);
  if (base) {
    const muda = Object.fromEntries(Object.entries(daAba).filter(([k, v]) => String(base.c[k] ?? '') !== String(v ?? '')));
    if (Object.keys(muda).length) { mudarCampos(BASE, base.id, muda); const nb = clientesBase().find(x => x.id === base.id); if (nb) aoSalvarBase(nb); }
    return;
  }
  const dup = duplicadoDe(daAba);
  if (dup) { mudarCampos(mod, r.id, { cid: dup.r.id }); return; }
  const nova = novoReg('cliente', null, { ...daAba, situacao: 'Ativo' });
  salvarReg(BASE, nova);
  mudarCampos(mod, r.id, { cid: nova.id });
  aoSalvarBase(nova);
}
/** Os dados de uma ficha da aba de trabalho no formato de Meus clientes. */
function deAbaParaBase(mod: ModId, c: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = { nome: c.nome ?? '' };
  if (c.telefone) out.telefone = c.telefone;
  if (c.email) out.email = c.email;
  if (c.contato) { out.contatoNome = c.contato; }
  const d = doc(c.documento);
  if (d) out.doc = d;
  if (d.length === 14 || (mod === 'portal' && c.contato)) out.tipo = 'Empresa';
  else if (!out.tipo) out.tipo = c.contato ? 'Empresa' : 'Pessoa';
  if (mod === 'freelancer' && c.endereco) out.enderecoLivre = c.endereco;
  return out;
}
/** Uma aba de trabalho deixa de mostrar este cliente (a pessoa excluiu lá): a lista única não recria a ficha nela. */
export function tirarDaAba(mod: ModId, r: Reg) {
  if (!r.c.cid) return;
  const base = clientesBase().find(x => x.id === r.c.cid);
  if (base) mudarCampos(BASE, base.id, { fora: [...new Set([...(base.c.fora ?? []), mod])] });
}
/** Excluir o cliente e todos os dados dele (texto 25, F3): as fichas ligadas e o que está dentro delas. */
export function excluirLigadas(cid: string) { for (const l of ligadas(cid)) excluirReg(l.mod, l.r.id); }

/** Juntar duas fichas do mesmo cliente (E5): fica a primeira, completada com a segunda; as ligações passam para ela. */
export function juntarClientes(fica: Reg, sai: Reg) {
  const c = { ...sai.c, ...Object.fromEntries(Object.entries(fica.c).filter(([, v]) => v !== '' && v != null && !(Array.isArray(v) && !v.length))) };
  if (Array.isArray(fica.c.etiquetas) || Array.isArray(sai.c.etiquetas)) c.etiquetas = [...new Set([...(fica.c.etiquetas ?? []), ...(sai.c.etiquetas ?? [])])];
  const notas = [fica.c.notas, sai.c.notas].map(x => String(x ?? '').trim()).filter(Boolean);
  if (notas.length > 1) { c.notas = notas.join('\n\n'); delete c.notas__rico; }
  salvarReg(BASE, { ...fica, c, anexos: [...(fica.anexos ?? []), ...(sai.anexos ?? [])], criado: fica.criado < sai.criado ? fica.criado : sai.criado });
  for (const l of ligadas(sai.id)) mudarCampos(l.mod, l.r.id, { cid: fica.id });
  excluirReg(BASE, sai.id);
  const nb = clientesBase().find(x => x.id === fica.id);
  if (nb) aoSalvarBase(nb);
}

/** Migração (uma vez): os clientes do Portal do Cliente e do Freelancer viram fichas de Meus clientes (ou são ligados a
 *  uma que já existe, se for o mesmo cliente). Avisa quantos vieram de cada aba e quantos duplicados foram juntados. */
export function migrarClientes(avisar = true) {
  const aj = getDoc('ajustes') as any;
  const contas: Record<string, number> = {};
  let juntados = 0;
  for (const a of ABAS_TRABALHO) {
    for (const r of regsDe(a.mod).filter(x => x.col === a.col && !x.c.cid && !ehDeOutro(x))) {
      const daAba = deAbaParaBase(a.mod, r.c);
      if (!String(daAba.nome ?? '').trim()) continue;
      const dup = duplicadoDe(daAba);
      if (dup) { mudarCampos(a.mod, r.id, { cid: dup.r.id }); juntados++; contas[a.mod] = (contas[a.mod] ?? 0) + 1; continue; }
      const nova = { ...novoReg('cliente', null, { ...daAba, situacao: 'Ativo' }), criado: r.criado };
      salvarReg(BASE, nova);
      mudarCampos(a.mod, r.id, { cid: nova.id });
      contas[a.mod] = (contas[a.mod] ?? 0) + 1;
    }
  }
  // as abas em uso ganham as fichas que faltarem (mesma lista em todas)
  for (const b of clientesBase()) aoSalvarBase(b);
  const total = Object.values(contas).reduce((s, n) => s + n, 0);
  if (!aj.clientesMigrados) setDoc('ajustes', (d: any) => ({ ...d, clientesMigrados: new Date().toISOString() }));
  if (avisar && total && !aj.clientesMigrados) setTimeout(() => Alert.alert(t('Meus clientes'), [
    t('Os seus clientes agora ficam numa lista só, usada por todas as abas de trabalho.'),
    contas.portal ? t('Portal do Cliente: {n} cliente(s).', { n: contas.portal }) : '',
    contas.freelancer ? t('Freelancer & Projetos: {n} cliente(s).', { n: contas.freelancer }) : '',
    juntados === 1 ? t('1 cliente estava em mais de uma aba e virou uma ficha só.') : juntados ? t('{n} eram o mesmo cliente em mais de uma aba e viraram uma ficha só.', { n: juntados }) : t('Nenhum cliente repetido.'),
    t('Nada foi apagado: documentos, pedidos, cartões e horas continuam onde estavam.'),
  ].filter(Boolean).join('\n\n')), 600);
  return { contas, juntados };
}

/** Texto 25 (A3): do paciente (Consultório) ou do aluno (Professor) para Meus clientes: SÓ o nome e o contato
 *  (nunca prontuário, evolução, notas ou dados de saúde). */
export function copiarParaClientes(c: { nome?: string; telefone?: string; email?: string }) {
  const dados = { nome: String(c.nome ?? '').trim(), telefone: String(c.telefone ?? '').trim(), email: String(c.email ?? '').trim() };
  if (!dados.nome) return;
  const dup = duplicadoDe(dados);
  if (dup) { Alert.alert(t('Já está em Meus clientes'), t('{nome} já está na lista.', { nome: String(dup.r.c.nome ?? '') })); return; }
  const nova = novoReg('cliente', null, { ...dados, tipo: 'Pessoa', situacao: 'Ativo', whatsapp: true, autoriza: false });
  salvarReg(BASE, nova);
  aoSalvarBase(nova);
  Alert.alert(t('Copiado para Meus clientes'), t('Só o nome e o contato foram copiados.'));
}
